import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import MyDialogContent from "@/components/MyDialogContent";
import { Dialog } from "@/components/ui/dialog";
import AxiosInstance from "@/utils/AxiosInstance";
import {
  formatDateTime12,
  getErrToast,
  getPromise,
  getPromiseToast,
} from "@/utils/helpers";
import type { ConflictCandidatePatient, ConflictRow } from "../types";
import { normalizeCandidatePatient } from "../api";
import { getConflictCallerSearchNumber } from "../utils";
import { Check, Loader2, Play } from "lucide-react";
import clsx from "clsx";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflict: ConflictRow | null;
  onResolved?: (conflictId: string) => void;
};

function patientDisplayName(p: ConflictCandidatePatient) {
  return [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ").trim() || "—";
}

function patientOrgName(p: ConflictCandidatePatient) {
  if (p.organizationName) return p.organizationName;
  const org = p.organizationId;
  if (org && typeof org === "object") return org.organizationName || "—";
  return "—";
}

function patientCode(p: ConflictCandidatePatient) {
  return String(p.patientId || p._id || "—");
}

function initials(p: ConflictCandidatePatient) {
  const first = (p.firstName || "").charAt(0);
  const last = (p.lastName || "").charAt(0);
  return (first + last).toUpperCase() || "?";
}

function formatDuration(conflict: ConflictRow) {
  if (conflict.duration) return String(conflict.duration);
  const seconds = Number(conflict.durationSeconds);
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export default function ResolveConflictDialog({
  open,
  onOpenChange,
  conflict,
  onResolved,
}: Props) {
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    null,
  );
  const [approving, setApproving] = useState(false);
  const [markingInvalid, setMarkingInvalid] = useState(false);
  const [fallbackPatients, setFallbackPatients] = useState<
    ConflictCandidatePatient[]
  >([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  const conflictCandidates = useMemo(() => {
    const list = conflict?.candidatePatients ?? conflict?.patients ?? [];
    return Array.isArray(list) ? list : [];
  }, [conflict]);

  const candidates =
    (false && conflictCandidates.length > 0) ? conflictCandidates : fallbackPatients;

  const phone = conflict?.externalNumber || conflict?.phoneNumber || "—";
  const callPhoneSearch = getConflictCallerSearchNumber(conflict);

  useEffect(() => {
    if (!open) {
      setSelectedPatientId(null);
      setApproving(false);
      setMarkingInvalid(false);
      setFallbackPatients([]);
      setLoadingPatients(false);
    }
  }, [open]);

  useEffect(() => {
    setSelectedPatientId(null);
  }, [conflict?._id]);

  useEffect(() => {
    if (!open || !conflict) return;
    if (conflictCandidates.length > 0 && false) {
      setFallbackPatients([]);
      setLoadingPatients(false);
      return;
    }

    const orgId =
      typeof conflict.organizationId === "object"
        ? conflict.organizationId?._id
        : conflict.organizationId;

    let cancelled = false;
    setLoadingPatients(true);

    const loadPatients = async () => {
      try {
        const { data }: any = await AxiosInstance().get("/patients", {
            params: {
              search: callPhoneSearch || undefined,
              // orgId: orgId || undefined,
              // enrollmentStatus: "enrolled",
              refreshKey: 0,
              skip: 0,
              limit: 100,
              // status: ["active", "inactive", "newly_added"],
            },
          });
        if (cancelled) return;
        const list = Array.isArray(data?.data?.patients)
          ? data.data.patients
          : [];
        setFallbackPatients(
          list
            .map(normalizeCandidatePatient)
            .filter((p: ConflictCandidatePatient) => p._id),
        );
      } catch {
        if (!cancelled) setFallbackPatients([]);
      } finally {
        if (!cancelled) setLoadingPatients(false);
      }
    };

    void loadPatients();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    conflict?._id,
    conflictCandidates.length,
    callPhoneSearch,
  ]);

  const callAt = conflict?.callAt || conflict?.createdAt;
  const recordingUrl = conflict?.recordingUrl || conflict?.attachmentUrl;
  const recordingName =
    conflict?.recordingName || conflict?.attachmentName || "recording.mp3";
  const durationLabel = conflict ? formatDuration(conflict) : null;

  const selectedPatient = candidates.find((p) => p._id === selectedPatientId);
  const hasNoPatients = !loadingPatients && candidates.length === 0;
  const isSubmitting = approving || markingInvalid;

  const handleApprove = async () => {
    if (!conflict?._id || !selectedPatient?._id) return;
    setApproving(true);
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      await AxiosInstance().put(
        `/aircall/unassigned-interactions/${conflict._id}`,
        {
          patientId: selectedPatient._id,
        },
      );
      resolveFn("Conflict resolved");
      onOpenChange(false);
      onResolved?.(conflict._id);
    } catch (err) {
      rejectFn(err);
      getErrToast(err);
    } finally {
      setApproving(false);
    }
  };

  const handleMarkInvalid = async () => {
    if (!conflict?._id) return;
    setMarkingInvalid(true);
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      await AxiosInstance().patch(
        `/aircall/unassigned-interactions/${conflict._id}/invalid`,
        { reason: "Wrong number / not a patient" },
      );
      resolveFn("Interaction marked as invalid");
      onOpenChange(false);
      onResolved?.(conflict._id);
    } catch (err) {
      rejectFn(err);
      getErrToast(err);
    } finally {
      setMarkingInvalid(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MyDialogContent
        heading="Resolve Conflict"
        className="!h-auto max-h-[90vh] w-[96dvw] sm:!max-w-3xl md:!max-w-4xl"
        bodyClassName="!overflow-hidden !flex !flex-col"
        headerComponent={
          <p className="text-sm text-muted-foreground teachers mt-1">
            This activity is linked to a shared phone number. Select the patient
            it belongs to.
          </p>
        }
      >
        {conflict ? (
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <div className="shrink-0 rounded-lg border border-grey bg-grey-light/40 px-4 py-3 space-y-1">
              <p className="text-sm font-medium text-[#111827] teachers">
                Call • {callAt ? formatDateTime12(callAt) : "—"} • {phone}
              </p>
              <p className="text-xs text-muted-foreground teachers flex flex-wrap gap-x-2 gap-y-1">
                {durationLabel ? <span>Duration {durationLabel}</span> : null}
                {durationLabel && recordingUrl ? <span>|</span> : null}
                {recordingUrl ? (
                  <a
                    href={recordingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Play className="h-3.5 w-3.5" />
                    {recordingName} (link)
                  </a>
                ) : null}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {loadingPatients ? (
                  <p className="text-sm text-muted-foreground col-span-full flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading patients...
                  </p>
                ) : hasNoPatients ? (
                  <div className="col-span-full space-y-2">
                    <p className="text-sm text-muted-foreground">
                      No patients found for this phone number.
                    </p>
                  </div>
                ) : (
                  candidates.map((patient, index) => {
                    const isSelected = selectedPatientId === patient._id;
                    const name = patientDisplayName(patient);
                    const code = patientCode(patient);
                    // const rows = [
                    //   { label: "Name", value: name },
                    //   {
                    //     label: "Organisation",
                    //     value: patientOrgName(patient),
                    //   },
                    //   {
                    //     label: "Phone",
                    //     value: patient.primaryNumber || "—",
                    //   },
                    //   {
                    //     label: "Status",
                    //     value: patient.status
                    //       ? String(patient.status).replace(/_/g, " ")
                    //       : "—",
                    //   },
                    // ];

                    return (
                      <button
                        key={patient._id || index}
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => setSelectedPatientId(patient._id)}
                        className={clsx(
                          "rounded-xl text-left overflow-hidden flex flex-col border transition-colors bg-white p-4",
                          isSelected
                            ? "border-primary ring-primary bg-primary/[0.03]"
                            : "border-[#E5E7EB] hover:border-[#D1D5DB]",
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <Avatar className="h-11 w-11 shrink-0">
                            <AvatarImage
                              src={
                                patient.photoUrl || patient.profileImage || ""
                              }
                              alt={name}
                            />
                            <AvatarFallback className="bg-[#DBEAFE] text-[#1D4ED8] teachers text-sm font-semibold">
                              {initials(patient)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-[#111827] teachers truncate">
                              {name}
                            </p>
                            <p className="text-[13px] text-muted-foreground teachers truncate">
                              {code}
                            </p>
                          </div>
                          <span
                            className={clsx(
                              "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors",
                              isSelected
                                ? "border-primary bg-primary text-white"
                                : "border-[#D1D5DB] bg-white text-transparent",
                            )}
                            aria-hidden
                          >
                            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-2 border-t border-[#E5E7EB] pt-3">
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              {hasNoPatients ? (
                <Button
                  type="button"
                  // variant="destructive"
                  disabled={isSubmitting}
                  onClick={handleMarkInvalid}
                >
                  {markingInvalid ? "Marking..." : "Mark as Invalid"}
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={!selectedPatientId || isSubmitting}
                  onClick={handleApprove}
                >
                  {approving ? "Approving..." : "Approve"}
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </MyDialogContent>
    </Dialog>
  );
}
